/* eslint-disable @typescript-eslint/no-unused-vars */

import React, { PureComponent, ReactNode, ErrorInfo } from 'react';
import baseLoadable, {
  LoadableComponent,
  OptionsWithResolver,
  OptionsWithoutResolver,
  DefaultComponent,
} from '@loadable/component';
import { timeout as promiseTimeout } from 'promise-timeout';
import { captureException, configureScope } from '@sentry/browser';
import { Severity } from '@sentry/types';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { DEBUG } from '../../../ReduxStorage/location/constants.web';

type BoundaryProps<InnerProps> = InnerProps;

interface BoundaryState {
  error?: Error;
}

const withErrorBoundary = <InnerProps,>(
  Wrapped: LoadableComponent<InnerProps>
): LoadableComponent<InnerProps> => {
  const Boundary = class ErrorBoundary extends PureComponent<
    BoundaryProps<InnerProps>,
    BoundaryState
  > {
    state: BoundaryState = {};

    static getDerivedStateFromError(error: Error): BoundaryState {
      // Update state so the next render will show the fallback UI.
      return { error };
    }

    componentDidCatch(error: Error, info: ErrorInfo): void {
      if (DEBUG) {
        configureScope((scope) => {
          scope.setLevel(Severity.Warning);
          // eslint-disable-next-line no-console
          console.error(error);
          scope.setExtra('component_stack', info.componentStack);
          captureException(error);
        });
      }
    }

    render(): ReactNode {
      const { error } = this.state;
      if (error) {
        return <div>Error: {error.message}</div>;
      }
      return <Wrapped {...this.props} />;
    }
  };
  return hoistNonReactStatics(Boundary, Wrapped);
};

interface Loadable {
  <Props, Module>(
    createImport: (props: Props) => Promise<Module>,
    options: OptionsWithResolver<Props, Module> & { timeout?: number }
  ): LoadableComponent<Props>;

  <Props>(
    createImport: (props: Props) => Promise<DefaultComponent<Props>>,
    options?: OptionsWithoutResolver<Props> & { timeout?: number }
  ): LoadableComponent<Props>;
}

// There are useful type annotations on the overloads above ^^
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const loadable: Loadable = (ctor: any, options: any = {}) => {
  /**
   * At this point, the `createImport` function passed has been transformed by
   * @loadable/babel-plugin into an object with sync/async hooks to load the code.
   * This is difficult to represent in typescript because the wrapper is invisible.
   */
  const { timeout = 30000, ...otherOptions } = options;
  if (ctor.importAsync) {
    // If the babel plugin has transformed the `createImport`
    return withErrorBoundary(
      baseLoadable(
        {
          ...ctor,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          importAsync: (props: any) =>
            promiseTimeout(ctor.importAsync(props), timeout),
        },
        otherOptions
      )
    );
  }
  return withErrorBoundary(
    baseLoadable((props) => promiseTimeout(ctor(props), timeout), otherOptions)
  );
};
